/**
 * UniCompare — generic side-by-side compare view for ANY 2-3 universities.
 * Extension of the FE-only compare page so a family can put an FE
 * college, a UK Russell Group and an Australian GO8 in the same view
 * ("Blackpool HND vs Northumbria BEng vs QUT Engineering").
 *
 * Route: `/compare/unis?ids=uk-oxford,au-cricos-00099z,uk-hesa-10000754`
 *
 * Renders a richer bento than the FE-only variant — brings back
 * `acceptance_rate`, `avg_starting_salary_usd`, `total_annual_cost`
 * because families comparing across tiers care about those numbers.
 * For directory-tier providers where those fields are null, we render
 * "—" gracefully (same policy as the uni detail bento).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowLeft, ArrowRight, Loader2, XCircle, GraduationCap, Link2, Check as CheckIcon } from "lucide-react";
import OfstedChip from "@/components/app/OfstedChip";
import PlacementsHintChip from "@/components/app/PlacementsHintChip";
import UniRankingChips from "@/components/app/university/UniRankingChips";
import { rankLabel } from "@/lib/rankLabel";
import { trackEvent } from "@/lib/analytics";

const TYPE_LABELS = {
  further_education_college: "FE college",
  further_education_college_group: "FE college group",
  university_centre: "University centre",
  specialist_conservatoire: "Conservatoire",
  conservatoire: "Conservatoire",
  specialist_he_provider: "Specialist HE",
  specialist_he_college: "Specialist HE",
  private_he_provider: "Private HE",
  university: "University",
  non_he_entity: "Non-HE entity",
};

export default function UniCompare() {
  const [params, setParams] = useSearchParams();
  const { format } = useCurrency();
  const idsRaw = params.get("ids") || "";
  const ids = useMemo(
    () => idsRaw.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3),
    [idsRaw]
  );
  const [state, setState] = useState({ loading: true, unis: [] });

  useEffect(() => {
    if (!ids.length) {
      setState({ loading: false, unis: [] });
      return;
    }
    setState({ loading: true, unis: [] });
    Promise.all(
      ids.map((id) =>
        api
          .get(`/universities/${encodeURIComponent(id)}`)
          .then((r) => r.data)
          .catch(() => null)
      )
    ).then((results) => {
      setState({ loading: false, unis: results.filter(Boolean) });
    });
  }, [idsRaw]);

  const removeId = (id) => {
    const remaining = ids.filter((x) => x !== id);
    if (remaining.length === 0) params.delete("ids");
    else params.set("ids", remaining.join(","));
    setParams(params, { replace: true });
  };

  const pct = (v) => (v != null && !Number.isNaN(v) ? `${(v * 100).toFixed(1)}%` : "—");
  // Unknown tuition makes the total incomplete rather than cheap.
  const totalCost = (u) =>
    u?.tuition_intl_annual_usd ? u.tuition_intl_annual_usd + (u?.accommodation_annual_usd || 0) : null;

  if (!ids.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <GraduationCap className="h-10 w-10 mx-auto text-stone-400" />
        <h1 className="mt-4 font-editorial text-3xl">Compare universities</h1>
        <p className="mt-2 text-stone-600 max-w-xl mx-auto">
          Put any 2 or 3 universities side-by-side — including FE colleges,
          Russell Group, US Ivies or Australian GO8. Answers the "which of
          these actually earns me more, costs me less, gets me in?" question
          in one view.
        </p>
        <Link
          to="/explore"
          data-testid="uni-compare-browse-link"
          className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-[#3a1476]"
        >
          Browse universities <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10" data-testid="uni-compare-page">
      <Link
        to="/explore"
        className="text-sm text-stone-500 hover:text-[#4C1D95] inline-flex items-center gap-1.5"
        data-testid="uni-compare-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Explore
      </Link>

      <div className="mt-4">
        <div className="label-uppercase text-[#4C1D95]">Compare</div>
        <div className="mt-1 flex flex-wrap items-baseline justify-between gap-3">
          <h1
            data-testid="uni-compare-heading"
            className="font-editorial text-3xl sm:text-4xl text-[#002147]"
          >
            {state.unis.length
              ? state.unis.map((u) => u.name).join("  vs  ")
              : "Loading…"}
          </h1>
          {state.unis.length >= 2 && <ShareShortLinkButton ids={ids} />}
        </div>
      </div>

      {state.loading && (
        <div className="mt-8 flex items-center gap-2 text-stone-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading universities…
        </div>
      )}

      {!state.loading && state.unis.length === 0 && (
        <div
          data-testid="uni-compare-empty"
          className="mt-8 rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-8 text-center text-stone-600"
        >
          None of the requested universities could be loaded.
        </div>
      )}

      {!state.loading && state.unis.length > 0 && (
        <div
          data-testid="uni-compare-grid"
          className={`mt-6 grid gap-4 grid-cols-1 ${
            state.unis.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"
          }`}
        >
          {state.unis.map((u) => {
            const total = totalCost(u);
            return (
              <div
                key={u.id}
                data-testid={`uni-compare-card-${u.id}`}
                className="rounded-lg border border-stone-200 bg-white p-5 relative"
              >
                <button
                  type="button"
                  onClick={() => removeId(u.id)}
                  data-testid={`uni-compare-remove-${u.id}`}
                  className="absolute top-3 right-3 text-stone-400 hover:text-rose-600"
                  aria-label={`Remove ${u.name}`}
                >
                  <XCircle className="h-4 w-4" />
                </button>

                <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 pr-8">
                  {u.country} ·{" "}
                  {TYPE_LABELS[u.institution_type] ||
                    (u.source_tier === "editorial" ? "University" : "Higher education provider")}
                </div>
                <div className="mt-1 font-editorial text-xl text-[#002147] leading-snug pr-8">
                  {u.name}
                </div>
                <div className="mt-0.5 text-[12px] text-stone-500">
                  {u.city || u.country}
                  {rankLabel(u) ? (
                    <span className="ml-2 text-stone-400 font-mono">
                      · {rankLabel(u)}
                    </span>
                  ) : null}
                </div>

                {u.ofsted_rating && (
                  <div className="mt-3">
                    <OfstedChip
                      rating={u.ofsted_rating}
                      inspectionDate={u.ofsted_inspection_date}
                    />
                  </div>
                )}

                {/* Placements hint — flags the regional placement
                    pipeline (London hubs / Milan finance / Madrid+Barça
                    …) so families comparing multi-country shortlists
                    see the pipeline story at a glance. Clicks land on
                    the uni's Outcomes tab; the per-firm deep-dive
                    itself lives on any degree detail page under the
                    uni via `<PlacementsDeepDiveCard>`. */}
                <div className="mt-2">
                  <PlacementsHintChip
                    degree={{ university_id: u.id }}
                    href={`/uni/${u.id}`}
                    testid={`uni-compare-placements-hint-${u.id}`}
                  />
                </div>

                {/* Subject-ranking chip strip — top 3 leaderboards
                    where this uni ranks. Anchors the abstract
                    "prestige" question to the specific subject
                    families are shortlisting for. */}
                <UniRankingChips uniId={u.id} limit={3} />

                {/* Bento — same 4 metrics as the uni detail page. */}
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-stone-100 pt-3">
                  <div>
                    <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                      Acceptance
                    </div>
                    <div
                      data-testid={`uni-compare-acceptance-${u.id}`}
                      className="font-mono text-lg font-semibold text-[#002147]"
                    >
                      {pct(u.acceptance_rate)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                      Total annual
                    </div>
                    <div
                      data-testid={`uni-compare-cost-${u.id}`}
                      className="font-mono text-lg font-semibold text-[#002147]"
                    >
                      {total ? format(total) : <span className="text-sm text-stone-500">Incomplete: tuition not available</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                      Grad. salary
                    </div>
                    <div
                      data-testid={`uni-compare-salary-${u.id}`}
                      className="font-mono text-lg font-semibold text-[#002147]"
                    >
                      {u.avg_starting_salary_usd
                        ? format(u.avg_starting_salary_usd)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                      Satisfaction
                    </div>
                    <div
                      data-testid={`uni-compare-satisfaction-${u.id}`}
                      className="font-mono text-lg font-semibold text-[#002147]"
                    >
                      {u.satisfaction_score != null ? `${u.satisfaction_score}/5` : "—"}
                    </div>
                  </div>
                </div>

                {u.description && (
                  <p
                    data-testid={`uni-compare-description-${u.id}`}
                    className="mt-4 text-[13px] text-stone-700 leading-snug line-clamp-[7]"
                  >
                    {u.description}
                  </p>
                )}

                <div className="mt-4 pt-3 border-t border-stone-100">
                  <Link
                    to={`/uni/${u.id}`}
                    data-testid={`uni-compare-full-link-${u.id}`}
                    className="text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
                  >
                    Full page →
                  </Link>
                </div>
              </div>
            );
          })}

          {state.unis.length < 3 && (
            <UniSearchSlot
              existingIds={ids}
              onPick={(uni) => {
                const next = [...ids, uni.id];
                params.set("ids", next.join(","));
                setParams(params, { replace: true });
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ShareShortLinkButton — turns the current compare URL into a
 * short `/c/aB3xY` slug via `POST /api/compare/short`, then copies
 * the full canonical URL to the clipboard. Idempotent — clicking
 * twice gets the same code.
 */
function ShareShortLinkButton({ ids }) {
  const [state, setState] = useState({ status: "idle", code: null });
  const handle = async () => {
    setState({ status: "loading", code: null });
    try {
      const { data } = await api.post("/compare/short", { ids: ids.join(",") });
      const full = `${window.location.origin}/c/${data.code}`;
      try {
        await navigator.clipboard.writeText(full);
      } catch (_e) {
        // Clipboard API can throw on non-HTTPS or old browsers;
        // fall back to a manual prompt.
        window.prompt("Copy this compare link:", full);
      }
      trackEvent("compare_short_created", { code: data.code, count: ids.length });
      setState({ status: "copied", code: data.code });
      setTimeout(() => setState({ status: "idle", code: data.code }), 2500);
    } catch (_e) {
      setState({ status: "error", code: null });
      setTimeout(() => setState({ status: "idle", code: null }), 2500);
    }
  };

  const label =
    state.status === "loading"
      ? "Shortening…"
      : state.status === "copied"
      ? "Link copied"
      : state.status === "error"
      ? "Try again"
      : "Copy share link";

  return (
    <button
      type="button"
      onClick={handle}
      disabled={state.status === "loading"}
      data-testid="uni-compare-share-btn"
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-underground-sc tracking-wider transition-colors " +
        (state.status === "copied"
          ? "border-emerald-600 bg-emerald-600 text-white"
          : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]")
      }
    >
      {state.status === "copied" ? (
        <CheckIcon className="h-3 w-3" />
      ) : (
        <Link2 className="h-3 w-3" />
      )}
      {label}
    </button>
  );
}

/**
 * UniSearchSlot — typeahead for adding a university to the compare
 * set without context-switching back to Explore. Queries the
 * `/api/universities?search=` endpoint with a 300ms debounce.
 * Rejects picks already in the compare set.
 */
function UniSearchSlot({ existingIds, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/universities?search=${encodeURIComponent(term)}&limit=8&view=card`);
        setResults(data || []);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div
      data-testid="uni-compare-search-slot"
      className="rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/50 p-4 min-h-[280px] relative"
    >
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-400 mb-2">
        Add another university
      </div>
      <input
        type="text"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Search e.g. Oxford, Melbourne, Blackpool…"
        data-testid="uni-compare-search-input"
        className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4C1D95]/30"
      />

      {open && q.trim().length >= 2 && (
        <div
          data-testid="uni-compare-search-results"
          className="absolute left-4 right-4 top-[86px] rounded-md border border-stone-200 bg-white shadow-lg max-h-[240px] overflow-y-auto z-20"
        >
          {loading && (
            <div className="p-3 text-[12px] text-stone-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="p-3 text-[12px] text-stone-500">
              No universities match "{q}".
            </div>
          )}
          {!loading &&
            results.map((u) => {
              const already = existingIds.includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  disabled={already}
                  onClick={() => {
                    if (already) return;
                    onPick(u);
                    setQ("");
                    setOpen(false);
                  }}
                  data-testid={`uni-compare-search-result-${u.id}`}
                  className={
                    "w-full text-left px-3 py-2 border-b border-stone-100 last:border-b-0 flex items-baseline justify-between gap-2 " +
                    (already
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-stone-50")
                  }
                >
                  <div className="min-w-0">
                    <div className="font-editorial text-[15px] text-[#002147] truncate">
                      {u.name}
                    </div>
                    <div className="text-[10px] font-mono text-stone-500">
                      {u.country}{u.city ? ` · ${u.city}` : ""}
                    </div>
                  </div>
                  {already && (
                    <div className="text-[10px] font-underground-sc tracking-wider text-stone-400 shrink-0">
                      Already added
                    </div>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
